import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { WithRouter, withRouter } from 'found';
import { compose } from 'redux';

import { fetchCollectionContents, fetchCollections } from '../actions/collections.actions';
import withAsyncData from '../components/utils/withAsyncData';
import { StoreType } from '../reducers';
import { createPaginationRequest } from '../utils/pagination';
import { Collection, Pagination } from '../types/AppContent';
import { selectCollectionWithContents, selectCollectionStatus } from '../reducers/collections.selectors';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { ampliTrackCollectionViewedEvent } from '../services/track.service';
import { selectUser } from '../reducers/user.selectors';
import CuratedCollectionOverview from '../components/CuratedCollectionOverview';

interface StateProps {
  collection?: Collection;
  collectionStatus: AsyncActionStatus;
  collectionPagination: Pagination;
  user: StoreType['user'];
}
interface DispatchProps {
  fetchCollectionContents: typeof fetchCollectionContents;
}
interface OwnProps {
  match: {
    params: {
      collectionId: string;
    };
  };
}

type CuratedCollectionItemListPageProps = DispatchProps & StateProps & OwnProps & WithRouter;

const CuratedCollectionItemListPage: React.FC<CuratedCollectionItemListPageProps> = ({
  match,
  collection,
  collectionStatus,
  ...props
}) => {
  const collectionId = match.params.collectionId;
  const user = useSelector(selectUser);

  useEffect(() => {
    props.fetchCollectionContents(collectionId, createPaginationRequest(props.collectionPagination));
    if (user && collection) {
      ampliTrackCollectionViewedEvent(collection);
    }
  }, [collectionId]);

  if (!collection) {
    return null;
  }

  return (
    <CuratedCollectionOverview
      collection={collection}
      collectionStatus={collectionStatus}
      collectionId={collectionId}
      collectionPagination={props.collectionPagination}
      loadNext={() => {
        return props.fetchCollectionContents(collectionId, createPaginationRequest({
          ...props.collectionPagination,
          offset: props.collectionPagination.offset + props.collectionPagination.limit,
        }));
      }}
    />
  );
};

const mapState = (state: StoreType, props: OwnProps): StateProps => ({
  collection: selectCollectionWithContents(state, props.match.params.collectionId),
  collectionStatus: selectCollectionStatus(state, props.match.params.collectionId),
  collectionPagination: state.collections.byId[props.match.params.collectionId] &&
    state.collections.byId[props.match.params.collectionId].pagination,
  user: state.user,
});

export default compose<React.FC<Record<string, unknown>>>(
  withRouter,
  connect(
    mapState,
    { fetchCollections, fetchCollectionContents },
  ),
  withAsyncData((props) => ({
    refetch: () => props.fetchCollections(),
    dataProp: 'collection',
    statusProp: 'collectionStatus'
  })),
)(CuratedCollectionItemListPage);
